import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';

/**
 * S7: Notification Rules Engine - Alert Evaluation
 * POST /api/alerts/evaluate - Evaluate all active alert rules against current data
 * GET  /api/alerts/evaluate - Get recent alert history
 * 
 * Safety rules (asks.txt 3.4):
 * - Alerts NEVER modify data
 * - Alerts NEVER auto-post
 * - Full traceability (context JSON with trigger reason)
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  const supabaseAdmin = getSupabaseAdminClient();

  // GET: Return alert history
  if (req.method === 'GET') {
    try {
      const { limit: queryLimit, status } = req.query;
      let query = supabaseAdmin
        .from('alert_history')
        .select('*, alert_rules(name, trigger_type, severity)')
        .order('created_at', { ascending: false })
        .limit(Number(queryLimit) || 50);

      if (status) {
        query = query.eq('delivery_status', status as string);
      }

      const { data: alerts, error } = await query;
      if (error) throw error;

      return res.status(200).json({ success: true, data: alerts });
    } catch (error: any) {
      return res.status(500).json({ error: 'Error al obtener historial de alertas.' });
    }
  }

  // POST: Evaluate rules and create alerts
  if (req.method === 'POST') {
    try {
      // Get all active alert rules
      const { data: rules, error: rulesError } = await supabaseAdmin
        .from('alert_rules')
        .select('*')
        .eq('enabled', true);

      if (rulesError) throw rulesError;
      if (!rules || rules.length === 0) {
        return res.status(200).json({ success: true, alerts_created: 0, message: 'No hay reglas activas.' });
      }

      const alertsCreated: any[] = [];

      for (const rule of rules) {
        try {
          const alerts = await evaluateRule(supabaseAdmin, rule);
          for (const alert of alerts) {
            const { error: insertError } = await supabaseAdmin.from('alert_history').insert({
              alert_rule_id: rule.id,
              trigger_data: alert.triggerData,
              severity: rule.severity,
              message: alert.message,
              recipients: rule.recipients,
              delivery_status: 'pending',
            });

            if (!insertError) {
              alertsCreated.push({
                rule_name: rule.name,
                severity: rule.severity,
                message: alert.message,
              });
            }
          }
        } catch (ruleError: any) {
          console.error(`[Alerts] Error evaluating rule ${rule.name}:`, ruleError.message);
        }
      }

      return res.status(200).json({
        success: true,
        alerts_created: alertsCreated.length,
        alerts: alertsCreated,
      });
    } catch (error: any) {
      console.error('[Alerts] Evaluation error:', error);
      return res.status(500).json({ error: 'Error al evaluar reglas de alertas.' });
    }
  }

  return res.status(405).json({ error: 'Method not allowed' });
}

interface AlertResult {
  message: string;
  triggerData: Record<string, any>;
}

/**
 * Evaluate a single alert rule against current data.
 * Each rule type has its own evaluation logic.
 * IMPORTANT: This function ONLY READS data, never modifies it.
 */
async function evaluateRule(supabaseAdmin: any, rule: any): Promise<AlertResult[]> {
  const results: AlertResult[] = [];
  const threshold = Number(rule.threshold_value) || 20;

  switch (rule.trigger_type) {
    case 'invoice_overdue': {
      // Check for documents pending > threshold days
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - threshold);

      const { data: overdueDocuments } = await supabaseAdmin
        .from('documentos')
        .select('id, nombre, cliente_id, fecha_subida, estado')
        .eq('estado', 'pendiente')
        .lt('fecha_subida', cutoffDate.toISOString())
        .limit(50);

      if (overdueDocuments && overdueDocuments.length > 0) {
        results.push({
          message: `${overdueDocuments.length} documento(s) pendiente(s) con más de ${threshold} días sin procesar.`,
          triggerData: {
            rule_type: 'invoice_overdue',
            threshold_days: threshold,
            count: overdueDocuments.length,
            document_ids: overdueDocuments.map((d: any) => d.id).slice(0, 10),
            evaluated_at: new Date().toISOString(),
          },
        });
      }
      break;
    }

    case 'expense_deviation': {
      // Check for expense deviation >threshold% vs previous period
      const now = new Date();
      const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
      const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const prevPeriod = `${prevYear}-${String(prevMonth).padStart(2, '0')}`;

      const { data: currentSummaries } = await supabaseAdmin
        .from('tax_summary')
        .select('cliente_id, base_imponible_recibidas, periodo')
        .eq('periodo', currentPeriod);

      const { data: prevSummaries } = await supabaseAdmin
        .from('tax_summary')
        .select('cliente_id, base_imponible_recibidas, periodo')
        .eq('periodo', prevPeriod);

      if (currentSummaries && prevSummaries) {
        const prevMap = new Map(prevSummaries.map((s: any) => [s.cliente_id, s]));

        for (const current of currentSummaries) {
          const prev = prevMap.get(current.cliente_id) as any;
          if (!prev) continue;

          const currentVal = Number(current.base_imponible_recibidas) || 0;
          const prevVal = Number(prev.base_imponible_recibidas) || 0;

          if (prevVal > 0) {
            const deviation = ((currentVal - prevVal) / prevVal) * 100;
            if (Math.abs(deviation) > threshold) {
              results.push({
                message: `Gastos del cliente ${current.cliente_id.substring(0, 8)}... ${deviation > 0 ? 'aumentaron' : 'disminuyeron'} un ${Math.abs(Math.round(deviation))}% respecto al período anterior.`,
                triggerData: {
                  rule_type: 'expense_deviation',
                  cliente_id: current.cliente_id,
                  current_period: currentPeriod,
                  previous_period: prevPeriod,
                  current_value: currentVal,
                  previous_value: prevVal,
                  deviation_percent: Math.round(deviation * 100) / 100,
                  threshold_percent: threshold,
                  evaluated_at: new Date().toISOString(),
                },
              });
            }
          }
        }
      }
      break;
    }

    case 'sync_failure': {
      // Check for recent failed sync runs
      const recentCutoff = new Date();
      recentCutoff.setHours(recentCutoff.getHours() - 24);

      const { data: failedSyncs } = await supabaseAdmin
        .from('cegid_sync_runs')
        .select('id, sync_type, error_message, started_at')
        .eq('status', 'failed')
        .gte('started_at', recentCutoff.toISOString());

      if (failedSyncs && failedSyncs.length > 0) {
        results.push({
          message: `${failedSyncs.length} sincronización(es) fallida(s) en las últimas 24 horas.`,
          triggerData: {
            rule_type: 'sync_failure',
            failed_count: failedSyncs.length,
            sync_ids: failedSyncs.map((s: any) => s.id),
            errors: failedSyncs.map((s: any) => s.error_message).filter(Boolean),
            evaluated_at: new Date().toISOString(),
          },
        });
      }
      break;
    }

    case 'duplicate_invoice': {
      // Check for potential duplicate invoices (same amount + date + client)
      const { data: recentDocs } = await supabaseAdmin
        .from('cegid_raw_records')
        .select('id, external_id, raw_data, created_at')
        .eq('record_type', 'factura')
        .order('created_at', { ascending: false })
        .limit(500);

      if (recentDocs && recentDocs.length > 1) {
        const seen = new Map<string, any>();
        const duplicates: any[] = [];

        for (const doc of recentDocs) {
          const data = doc.raw_data || {};
          const key = `${data.nif || ''}_${data.totalFactura || ''}_${data.fecha || ''}`;
          if (key === '__' || key === '_0_') continue;

          if (seen.has(key)) {
            duplicates.push({
              original: seen.get(key).id,
              duplicate: doc.id,
              key,
            });
          } else {
            seen.set(key, doc);
          }
        }

        if (duplicates.length > 0) {
          results.push({
            message: `${duplicates.length} posible(s) factura(s) duplicada(s) detectada(s).`,
            triggerData: {
              rule_type: 'duplicate_invoice',
              duplicate_count: duplicates.length,
              duplicates: duplicates.slice(0, 10),
              evaluated_at: new Date().toISOString(),
            },
          });
        }
      }
      break;
    }

    case 'company_in_losses': {
      // Check for companies with consecutive periods of losses
      const { data: financialRecords } = await supabaseAdmin
        .from('datos_financieros')
        .select('cliente_id, periodo, resultado')
        .order('periodo', { ascending: false })
        .limit(200);

      if (financialRecords && financialRecords.length > 0) {
        // Group by cliente_id, keep 2 most recent periods
        const clientePeriods = new Map<string, { periodo: string; resultado: number }[]>();
        for (const record of financialRecords) {
          const existing = clientePeriods.get(record.cliente_id) || [];
          if (existing.length < 2) {
            existing.push({ periodo: record.periodo, resultado: Number(record.resultado) || 0 });
            clientePeriods.set(record.cliente_id, existing);
          }
        }

        const clientesEnPerdidas: string[] = [];
        clientePeriods.forEach((periods, clienteId) => {
          if (periods.length === 2 && periods[0].resultado < 0 && periods[1].resultado < 0) {
            clientesEnPerdidas.push(clienteId);
          }
        });

        if (clientesEnPerdidas.length > 0) {
          results.push({
            message: `${clientesEnPerdidas.length} empresa(s) con pérdidas en los últimos 2 períodos consecutivos.`,
            triggerData: {
              rule_type: 'company_in_losses',
              count: clientesEnPerdidas.length,
              cliente_ids: clientesEnPerdidas,
              evaluated_at: new Date().toISOString(),
            },
          });
        }
      }
      break;
    }

    case 'income_drop': {
      // Check for income drop > threshold% vs previous period
      const now = new Date();
      const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
      const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const prevPeriod = `${prevYear}-${String(prevMonth).padStart(2, '0')}`;

      const { data: currentIncomes } = await supabaseAdmin
        .from('datos_financieros')
        .select('cliente_id, ingresos, periodo')
        .eq('periodo', currentPeriod);

      const { data: prevIncomes } = await supabaseAdmin
        .from('datos_financieros')
        .select('cliente_id, ingresos, periodo')
        .eq('periodo', prevPeriod);

      if (currentIncomes && prevIncomes) {
        const prevMap = new Map(prevIncomes.map((s: any) => [s.cliente_id, s]));

        for (const current of currentIncomes) {
          const prev = prevMap.get(current.cliente_id) as any;
          if (!prev) continue;

          const currentVal = Number(current.ingresos) || 0;
          const prevVal = Number(prev.ingresos) || 0;

          if (prevVal > 0) {
            const dropPercent = ((prevVal - currentVal) / prevVal) * 100;
            if (dropPercent > threshold) {
              results.push({
                message: `Ingresos del cliente ${current.cliente_id.substring(0, 8)}... cayeron un ${Math.round(dropPercent)}% respecto al período anterior.`,
                triggerData: {
                  rule_type: 'income_drop',
                  cliente_id: current.cliente_id,
                  current_period: currentPeriod,
                  previous_period: prevPeriod,
                  current_value: currentVal,
                  previous_value: prevVal,
                  drop_percent: Math.round(dropPercent * 100) / 100,
                  threshold_percent: threshold,
                  evaluated_at: new Date().toISOString(),
                },
              });
            }
          }
        }
      }
      break;
    }

    case 'high_vat_payable': {
      // Check for clients with IVA resultado above threshold (EUR)
      const now = new Date();
      const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

      const { data: taxRecords } = await supabaseAdmin
        .from('tax_summary')
        .select('cliente_id, iva_resultado, periodo')
        .eq('periodo', currentPeriod);

      if (taxRecords) {
        for (const record of taxRecords) {
          const ivaResultado = Number(record.iva_resultado) || 0;
          if (ivaResultado > threshold) {
            results.push({
              message: `IVA a pagar del cliente ${record.cliente_id.substring(0, 8)}... es de ${ivaResultado.toFixed(2)}€, superando el umbral de ${threshold}€.`,
              triggerData: {
                rule_type: 'high_vat_payable',
                cliente_id: record.cliente_id,
                periodo: currentPeriod,
                iva_resultado: ivaResultado,
                threshold_value: threshold,
                evaluated_at: new Date().toISOString(),
              },
            });
          }
        }
      }
      break;
    }

    case 'document_rejected': {
      // Check for recently rejected documents within threshold days
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - threshold);

      const { data: rejectedDocs } = await supabaseAdmin
        .from('documentos')
        .select('id, cliente_id, nombre, fecha_subida')
        .eq('estado', 'rechazado')
        .gte('fecha_subida', cutoffDate.toISOString())
        .limit(50);

      if (rejectedDocs && rejectedDocs.length > 0) {
        results.push({
          message: `${rejectedDocs.length} documento(s) rechazado(s) en los últimos ${threshold} días.`,
          triggerData: {
            rule_type: 'document_rejected',
            count: rejectedDocs.length,
            document_ids: rejectedDocs.map((d: any) => d.id).slice(0, 10),
            evaluated_at: new Date().toISOString(),
          },
        });
      }
      break;
    }
  }

  return results;
}

export default withAuth(handler, ['admin']);
